import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { Lightbulb } from 'lucide-react';

const PROGRESS_KEY = 'sf_course_progress';

interface Lesson {
  id: number;
  title: string;
  estimatedMinutes: number;
  content: string;
  whyItMatters: string;
  exercise: {
    type: 'rating' | 'fill-in' | 'calculation' | 'multiple-choice' | 'free-text' | 'action-plan';
    prompt: string;
    options?: string[];
    correctAnswer?: number;
    explanation?: string;
  };
}

const LESSONS: Lesson[] = [
  {
    id: 1, title: "What makes a startup idea investable?", estimatedMinutes: 10,
    content: `## The 3 Ingredients Investors Look For

An investable startup idea has three qualities:

**1. Large, growing market** — Investors want 10x returns, which requires a large market. "The addressable market is $50B" matters more than "we have a clever product."

**2. Clear, painful problem** — The problem must be urgent enough that users will pay to solve it *now*. Not a vitamin, a painkiller.

**3. Defensible differentiation** — Why can't a larger competitor copy you in 6 months? Network effects, proprietary data, switching costs, and brand are moats.

## What Kills Ideas Early
- Building a solution looking for a problem ("we built X, now who needs it?")
- Markets too small to support a venture-scale business
- No insight into *why you* specifically should win this market`,
    whyItMatters: "Most founders jump straight to building. But a structurally weak idea — no matter how well executed — can't become a fundable business. Getting this right before writing code saves months.",
    exercise: {
      type: 'rating',
      prompt: 'Rate these 3 ideas on a scale of 1-5 for investability, then see the expert ratings.',
    },
  },
  {
    id: 2, title: "Defining your target segment", estimatedMinutes: 8,
    content: `## Why "Everyone" Is Nobody

"Our target market is everyone who has a smartphone" — this statement has killed more startups than any technical failure.

**The ICP (Ideal Customer Profile) framework:**

| Attribute | B2C Example | B2B Example |
|-----------|-------------|-------------|
| Demographics | 25-40, urban, $60K+ income | 10-200 employee company |
| Psychographics | Values efficiency, early adopter | Has a dedicated ops manager |
| Problem trigger | Just started freelancing | Missed a deadline due to process |
| Current solution | Uses spreadsheets | Uses Slack + email chaos |

**The narrower the ICP, the stronger the product-market fit signals.**

Start with a segment of 1,000 people you can reach. Nail it. Then expand.`,
    whyItMatters: "Tight targeting means better word-of-mouth, higher conversion rates, and a clear 'who is this for' story for investors.",
    exercise: {
      type: 'fill-in',
      prompt: 'Complete the ICP template for your startup idea. Be specific — avoid vague descriptors.',
    },
  },
  {
    id: 3, title: "The problem > solution framework", estimatedMinutes: 12,
    content: `## Start With the Problem, Not the Solution

Most founders describe their startup like this:
> "We built a mobile app that uses AI to manage your calendar."

Investors hear: "We built a solution and hope someone has this problem."

**The right sequence:**
1. **Problem**: Freelancers lose 3+ hours/week on manual invoicing
2. **Why now**: Remote work explosion means 10M new freelancers since 2020
3. **Why this matters**: At $100/hour billing rate, that's $30K/year in lost time per freelancer
4. **Solution**: An app that auto-generates invoices from communication data in 30 seconds

**The "bad → good" rewrite exercise:**

❌ Bad: "We help people with time management"
✅ Good: "Freelance designers lose 3 hours/week on invoicing. We eliminate that in 30 seconds."

The good version quantifies the pain, names the victim, and is specific.`,
    whyItMatters: "Investors fund problems, not solutions. When you lead with the problem and quantify it, you establish urgency — the only reason anyone buys anything.",
    exercise: {
      type: 'fill-in',
      prompt: 'Rewrite a vague problem statement into a specific, quantified one.',
    },
  },
  {
    id: 4, title: "Unit economics that matter", estimatedMinutes: 15,
    content: `## The 3 Numbers Every Founder Must Know

**LTV (Lifetime Value)**
\`\`\`
LTV = (Price - Variable Cost) / Monthly Churn Rate
\`\`\`
If you charge $49/mo, have $5 variable costs, and 5% churn:
LTV = ($49 - $5) / 0.05 = **$880**

**CAC (Customer Acquisition Cost)**
\`\`\`
CAC = Total Sales & Marketing Spend / New Customers Acquired
\`\`\`
If you spent $10K and got 100 customers: CAC = **$100**

**LTV:CAC Ratio**
- < 1 : You're losing money on every customer
- 1-3 : Barely viable
- 3x+ : **Healthy — this is the target**
- 5x+ : Very efficient growth machine

In our example: $880 LTV / $100 CAC = **8.8x** — excellent.

**Payback Period**: How many months to recoup CAC?
= CAC / (Monthly Revenue × Gross Margin %)
= $100 / ($49 × 90%) ≈ **2.3 months** — also excellent.`,
    whyItMatters: "Unit economics determine whether your business model makes money at scale. A bad LTV:CAC ratio means every new customer destroys value. Investors look at this on slide 4.",
    exercise: {
      type: 'calculation',
      prompt: 'Calculate LTV, CAC, and LTV:CAC ratio for this scenario: Price $29/mo, variable cost $3/user, churn 8%, marketing spend $5,000/mo, 50 new customers/mo.',
    },
  },
  {
    id: 5, title: "Reading your financial projections", estimatedMinutes: 10,
    content: `## What the Numbers Actually Tell You

When you look at a projection chart, here's what to find:

**Break-even Month**: The first month where cumulative cash flow turns positive. This answers: "When do you stop burning money?"

**Burn Rate**: Monthly cash consumed when not yet profitable. Investors want to know: "How long does your funding last?"

**ARR (Annual Recurring Revenue)**: Monthly Revenue × 12 at the end of your projection period. This answers: "What size business are you building?"

**Gross Margin**: (Revenue - Variable Costs) / Revenue × 100
- SaaS: Aim for 70-80%+
- Marketplace: 10-25% is normal
- Hardware: 30-50%

**What investors look for:**
1. Is the growth rate realistic given your acquisition strategy?
2. Does break-even happen before you run out of money?
3. Are the unit economics improving as you scale?`,
    whyItMatters: "Projections are a story told with numbers. If you can't read your own projections, you can't defend them — and investors will test you.",
    exercise: {
      type: 'multiple-choice',
      prompt: 'A startup has: 200 users in month 12, $49 price, $5,000 fixed costs. What is their Month 12 gross margin if variable cost per user is $5?',
      options: ['76%', '90%', '80%', '61%'],
      correctAnswer: 0,
      explanation: 'Revenue = 200 × $49 = $9,800. Variable Costs = 200 × $5 = $1,000. Gross Profit = $8,800. Gross Margin = $8,800 / $9,800 = 89.8% ≈ 90%. The closest answer is 90%.',
    },
  },
  {
    id: 6, title: "How to run a beta test", estimatedMinutes: 12,
    content: `## The 5 Rules of Effective Beta Testing

**Rule 1: Recruit specifically** — Don't ask friends. Find people who actually have the problem. A freelance designer beats 10 non-designer friends.

**Rule 2: Set expectations upfront** — "This is an early product. Expect rough edges. Your feedback shapes what we build next."

**Rule 3: Observe, don't explain** — When a user gets stuck, resist helping. Watch where they struggle. That's your UX data.

**Rule 4: Ask about the past, not the future** — Bad: "Would you pay $20/month for this?" Good: "How much do you currently spend on invoicing tools?"

**Rule 5: Track behavior, not opinions** — Do they actually come back? What's the first week retention? That's more honest than NPS.

## The Minimum Beta Feedback You Need
- 5 sessions with your exact ICP
- 3 specific feature requests that overlap between users
- 1 surprising insight that changes your roadmap
- NPS score from all users`,
    whyItMatters: "Beta testing done wrong gives you false confidence ('my friends love it!'). Done right, it saves you 3 months of building the wrong thing.",
    exercise: {
      type: 'free-text',
      prompt: 'Write 5 specific questions you would ask a beta user of your startup. For each, explain what insight you\'re trying to get.',
    },
  },
  {
    id: 7, title: "Interpreting NPS score", estimatedMinutes: 8,
    content: `## What NPS Actually Tells You

**Net Promoter Score = % Promoters − % Detractors**

| Score | Meaning |
|-------|---------|
| < 0 | More detractors than promoters — critical problem |
| 0-30 | OK but room for improvement |
| 30-70 | Good — most successful products |
| 70+ | World-class (Apple, Netflix) |

**The 3 segments:**
- **Promoters (9-10)**: Will recommend you. Drive organic growth.
- **Passives (7-8)**: Satisfied but not enthusiastic. Won't recommend.
- **Detractors (0-6)**: Risk negative word-of-mouth. Need to understand why.

## NPS Is Not Enough

Always pair NPS with open-ended "why" questions. An NPS of +25 with 40% "pricing" complaints tells a different story than +25 with 40% "feature requests."

**Action thresholds:**
- NPS < 0: Stop acquiring users. Fix the product.
- NPS 0-30: Interview every detractor. Find the root cause.
- NPS 30-50: Scale acquisition carefully. Monitor as you grow.
- NPS 50+: Accelerate. You have product-market fit signals.`,
    whyItMatters: "NPS is the single most predictive metric of word-of-mouth growth. A +50 NPS means your users sell for you. A negative NPS means growth is expensive and fragile.",
    exercise: {
      type: 'free-text',
      prompt: 'Given NPS data: 5 promoters (avg comment: "fast, love the auto-fill"), 3 passives (avg: "it works but the mobile app is slow"), 2 detractors (avg: "too expensive for what it does"). Identify 2 specific improvements to make and why.',
    },
  },
  {
    id: 8, title: "The investor narrative", estimatedMinutes: 15,
    content: `## The Anatomy of a 3-Minute Investor Pitch

Every successful pitch follows a story arc:

**Part 1 — The World As It Is (30 seconds)**
Establish the problem with a specific, believable statistic or story.
> "There are 59 million freelancers in the US. The average one wastes 3 hours a week on manual invoicing — that's $30K/year in lost billing time."

**Part 2 — The Insight (30 seconds)**
Why this problem is solvable *now*, by *you*.
> "Slack, email, and project tools now hold all the context needed to generate an invoice automatically. But no tool connects them."

**Part 3 — Your Solution (1 minute)**
Show, don't just tell. Demo if possible.
> "InvoiceZap reads your Slack threads and Figma comments, extracts deliverables and time, and generates a ready-to-send invoice in 30 seconds."

**Part 4 — Traction & Numbers (30 seconds)**
Your 3 most compelling numbers.
> "In 8 weeks: 47 beta users, NPS +42, $2,300 MRR, 89% week-2 retention."

**Part 5 — The Ask (30 seconds)**
Clear, specific, justified.
> "We're raising $500K to hire 2 engineers and reach $25K MRR in 12 months."`,
    whyItMatters: "Investors hear 1,000 pitches. The ones they fund tell a clear story with a beginning (problem), middle (your insight), and end (traction + ask). Structure beats enthusiasm.",
    exercise: {
      type: 'free-text',
      prompt: 'Write a 3-sentence investor pitch for your startup using the formula: [Problem with data] + [Your unique insight] + [Traction/ask]. Each sentence should be under 25 words.',
    },
  },
  {
    id: 9, title: "Common startup mistakes", estimatedMinutes: 10,
    content: `## The 7 Mistakes That Kill Startups

**1. Building before validating** — 42% of startups fail due to no market need. Talk to 20 potential customers before writing code.

**2. The "if you build it they will come" fallacy** — Distribution is as important as product. Your GTM strategy needs as much thought as your features.

**3. Hiring too fast** — The #1 cause of runway issues. Founders who hire 5 people at month 3 on a $300K raise are out of money in 9 months.

**4. Solving a problem you don't understand** — "I want to disrupt healthcare" without domain expertise leads to regulatory traps and wrong assumptions.

**5. Premature optimization** — Rewriting your backend for "scale" at 50 users. Focus on retention and PMF, not infrastructure.

**6. Pricing too low** — New founders underprice by fear. Charging $50/mo instead of $100/mo means you need 2x the customers for the same revenue.

**7. Ignoring churn** — Growing at 20%/mo with 15% monthly churn means your user base is barely growing. Churn is the silent killer.`,
    whyItMatters: "These mistakes are predictable and preventable. Most founders who fail had warning signs months before — but were too busy building to notice.",
    exercise: {
      type: 'multiple-choice',
      prompt: 'A startup has 20% monthly user growth but 15% monthly churn. After 12 months starting with 100 users, approximately how many users do they have?',
      options: ['~330 users', '~820 users', '~120 users', '~1,200 users'],
      correctAnswer: 0,
      explanation: 'Net growth = (1 + 0.20) × (1 - 0.15) = 1.02 per month. After 12 months: 100 × (1.02)^12 ≈ 127 users. The closest option is ~120-130. Churn eats nearly all growth.',
    },
  },
  {
    id: 10, title: "Your 30-day action plan", estimatedMinutes: 20,
    content: `## The First 30 Days After This Course

Most founders finish learning and immediately go back to building. That's the trap. Use this 30-day sprint to validate before building.

**Week 1 — Problem Validation**
- Identify 20 people who match your ICP
- Schedule 15-minute problem interviews (not product demos)
- Goal: hear the same problem described 5+ times unprompted

**Week 2 — Solution Concept Testing**
- Build a landing page describing the solution (no product yet)
- Drive 50 visits to it
- Count signups or waitlist adds
- Goal: 20%+ conversion = real demand signal

**Week 3 — First Version**
- Build the smallest possible thing that solves the core problem
- 1 feature, not 10
- Goal: something 3 users can actually use

**Week 4 — Beta Launch**
- Onboard 5 users from your waitlist
- Run the StartupScore beta feedback process
- Goal: NPS score and 3 specific improvement requests

**Key principle**: Move fast, but move in the right direction. A validated direction is worth more than 1,000 lines of unvalidated code.`,
    whyItMatters: "Information without action is just entertainment. This 30-day plan converts what you've learned into validated progress that makes your startup fundable.",
    exercise: {
      type: 'action-plan',
      prompt: 'Fill in your personal 30-day action plan with specific tasks, dates, and success metrics for each week.',
    },
  },
];

interface Progress {
  completedLessons: number[];
  exerciseAnswers: Record<number, string>;
}

export default function CoursePage() {
  const [searchParams] = useSearchParams();
  const lessonRef = useRef<HTMLDivElement>(null);
  const [progress, setProgress] = useState<Progress>(() => {
    const saved = localStorage.getItem(PROGRESS_KEY);
    return saved ? JSON.parse(saved) : { completedLessons: [], exerciseAnswers: {} };
  });
  const [selectedLesson, setSelectedLesson] = useState<Lesson>(() => {
    // ?lesson=N URL param auto-selects lesson
    const lessonParam = parseInt(searchParams.get('lesson') ?? '', 10);
    if (!isNaN(lessonParam)) {
      const found = LESSONS.find(l => l.id === lessonParam);
      if (found) return found;
    }
    return LESSONS[0];
  });
  const [answer, setAnswer] = useState('');
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [showAnswer, setShowAnswer] = useState(false);

  useEffect(() => { document.title = 'Learning Center — StartupScore'; }, []);

  useEffect(() => {
    localStorage.setItem(PROGRESS_KEY, JSON.stringify(progress));
  }, [progress]);

  // Auto-scroll to lesson content when lesson param changes or on mount
  useEffect(() => {
    setTimeout(() => {
      lessonRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 200);
  }, [selectedLesson.id]);

  useEffect(() => {
    const saved = progress.exerciseAnswers[selectedLesson.id] ?? '';
    setAnswer(typeof saved === 'string' ? saved : '');
    setSelectedOption(null);
    setShowAnswer(false);
  }, [selectedLesson.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const markComplete = () => {
    if (!progress.completedLessons.includes(selectedLesson.id)) {
      const updated = { ...progress, completedLessons: [...progress.completedLessons, selectedLesson.id] };
      setProgress(updated);
    }
    // Move to next lesson if available
    const nextIdx = LESSONS.findIndex((l) => l.id === selectedLesson.id) + 1;
    if (nextIdx < LESSONS.length) {
      setSelectedLesson(LESSONS[nextIdx]);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const saveAnswer = (val: string) => {
    setAnswer(val);
    setProgress((prev) => ({
      ...prev,
      exerciseAnswers: { ...prev.exerciseAnswers, [selectedLesson.id]: val },
    }));
  };

  const completionPercent = Math.round((progress.completedLessons.length / LESSONS.length) * 100);
  const isCompleted = progress.completedLessons.includes(selectedLesson.id);
  const nextLesson = LESSONS[LESSONS.findIndex((l) => l.id === selectedLesson.id) + 1];

  return (
    <div className="flex flex-col lg:flex-row gap-8">
      {/* ── Sidebar: Lesson List ───────────────────────────── */}
      <div className="lg:w-72 shrink-0">
        <div className="sticky top-6">
          <h2 className="text-xl font-bold text-white mb-2">Course</h2>

          {/* Progress */}
          <div className="mb-4">
            <div className="flex justify-between text-sm text-gray-400 mb-1">
              <span>{progress.completedLessons.length}/{LESSONS.length} complete</span>
              <span>{completionPercent}%</span>
            </div>
            <div className="h-2 bg-[#2a2a3e] rounded-full overflow-hidden">
              <div className="h-full bg-brand-500 rounded-full transition-all duration-500" style={{ width: `${completionPercent}%` }} />
            </div>
          </div>

          {/* Lesson list */}
          <nav className="space-y-1">
            {LESSONS.map((lesson, i) => {
              const done = progress.completedLessons.includes(lesson.id);
              const available = i === 0 || progress.completedLessons.includes(LESSONS[i - 1].id);
              return (
                <button
                  key={lesson.id}
                  onClick={() => available && setSelectedLesson(lesson)}
                  disabled={!available}
                  className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-colors ${
                    selectedLesson.id === lesson.id
                      ? 'bg-brand-500/20 border border-brand-500/30 text-brand-500'
                      : available
                        ? 'hover:bg-white/5 text-gray-400 hover:text-white border border-transparent'
                        : 'text-gray-600 cursor-not-allowed border border-transparent'
                  }`}
                >
                  <span className="inline-flex items-center gap-2">
                    <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs flex-shrink-0 ${
                      done ? 'bg-green-500 text-white' : 'bg-[#2a2a3e] text-gray-500'
                    }`}>
                      {done ? '✓' : lesson.id}
                    </span>
                    <span className="line-clamp-2">{lesson.title}</span>
                  </span>
                </button>
              );
            })}
          </nav>

          {/* Certificate */}
          {completionPercent === 100 && (
            <div className="mt-4 card border-yellow-500/50 text-center">
              <div className="text-3xl mb-2">🏆</div>
              <p className="text-yellow-400 font-bold text-sm">Course Complete!</p>
              <p className="text-gray-500 text-xs mt-1">You've completed all 10 lessons</p>
            </div>
          )}
        </div>
      </div>

      {/* ── Main: Lesson Content ───────────────────────────── */}
      <div className="flex-1 min-w-0" ref={lessonRef}>
        <div className="card mb-6">
          {/* Header */}
          <div className="flex items-start justify-between mb-1">
            <h1 className="text-2xl font-bold text-white">{selectedLesson.title}</h1>
            {isCompleted && <span className="badge bg-green-900/50 text-green-400 shrink-0 ml-2">✓ Complete</span>}
          </div>
          <p className="text-gray-500 text-sm mb-6">⏱ {selectedLesson.estimatedMinutes} min read</p>

          {/* Content */}
          <div className="prose prose-invert prose-sm max-w-none
            [&_h2]:text-white [&_h2]:font-bold [&_h2]:mt-6 [&_h2]:mb-3
            [&_h3]:text-gray-200 [&_h3]:font-semibold
            [&_p]:text-gray-300 [&_p]:leading-relaxed
            [&_ul]:text-gray-300 [&_li]:mb-1
            [&_strong]:text-white [&_strong]:font-semibold
            [&_code]:bg-[#0f0f1a] [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-brand-500
            [&_pre]:bg-[#0f0f1a] [&_pre]:p-4 [&_pre]:rounded-xl [&_pre]:overflow-x-auto
            [&_table]:w-full [&_td]:py-1.5 [&_td]:px-3 [&_th]:py-1.5 [&_th]:px-3 [&_th]:text-left [&_th]:text-gray-400 [&_th]:font-medium
            [&_tr]:border-b [&_tr]:border-[#2a2a3e]
            [&_blockquote]:border-l-4 [&_blockquote]:border-brand-500 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-gray-400">
            <ReactMarkdown>{selectedLesson.content}</ReactMarkdown>
          </div>

          {/* Why it matters */}
          <div className="mt-6 bg-brand-500/10 border border-brand-500/20 rounded-xl p-4">
            <p className="text-brand-500 font-medium text-sm mb-1 flex items-center gap-1.5"><Lightbulb size={14} className="text-yellow-400" /> Why this matters</p>
            <p className="text-gray-300 text-sm">{selectedLesson.whyItMatters}</p>
          </div>
        </div>

        {/* ── Exercise ───────────────────────────────────────── */}
        <div className="card mb-6">
          <h2 className="text-lg font-bold text-white mb-1">Exercise</h2>
          <p className="text-gray-400 text-sm mb-4">{selectedLesson.exercise.prompt}</p>

          {/* Multiple choice */}
          {selectedLesson.exercise.type === 'multiple-choice' && selectedLesson.exercise.options && (
            <div className="space-y-2">
              {selectedLesson.exercise.options.map((opt, i) => (
                <button
                  key={i}
                  onClick={() => { setSelectedOption(i); setShowAnswer(false); }}
                  className={`w-full text-left px-4 py-3 rounded-lg border text-sm transition-colors ${
                    selectedOption === i
                      ? showAnswer
                        ? i === selectedLesson.exercise.correctAnswer ? 'bg-green-900/30 border-green-500 text-green-300' : 'bg-red-900/30 border-red-500 text-red-300'
                        : 'bg-brand-500/20 border-brand-500 text-white'
                      : 'border-[#2a2a3e] text-gray-300 hover:border-gray-500'
                  }`}
                >
                  <span className="text-gray-500 mr-2">{String.fromCharCode(65 + i)}.</span> {opt}
                </button>
              ))}
              {selectedOption !== null && !showAnswer && (
                <button onClick={() => setShowAnswer(true)} className="btn-primary text-sm mt-2">Check Answer</button>
              )}
              {showAnswer && (
                <div className="mt-3 bg-[#0f0f1a] rounded-xl p-4">
                  <p className={`font-medium text-sm mb-1 ${selectedOption === selectedLesson.exercise.correctAnswer ? 'text-green-400' : 'text-red-400'}`}>
                    {selectedOption === selectedLesson.exercise.correctAnswer ? '✅ Correct!' : '❌ Not quite'}
                  </p>
                  <p className="text-gray-300 text-sm">{selectedLesson.exercise.explanation}</p>
                </div>
              )}
            </div>
          )}

          {/* Free text / fill-in / action plan */}
          {['free-text', 'fill-in', 'rating', 'calculation', 'action-plan'].includes(selectedLesson.exercise.type) && (
            <div>
              <textarea
                value={answer}
                onChange={(e) => saveAnswer(e.target.value)}
                rows={6}
                placeholder="Write your answer here..."
                className="input-field resize-none"
              />
              {selectedLesson.exercise.type === 'action-plan' && (
                <p className="text-xs text-gray-500 mt-2">Include: specific task, target date, and how you'll know it's done.</p>
              )}
            </div>
          )}
        </div>

        {/* ── Actions ─────────────────────────────────────────── */}
        <div className="flex items-center justify-between">
          {!isCompleted ? (
            <button onClick={markComplete} className="btn-primary flex items-center gap-2">
              ✓ Mark as Complete {nextLesson && `→ ${nextLesson.title}`}
            </button>
          ) : (
            <div className="flex items-center gap-2 text-green-400 text-sm">
              <span>✓ Completed</span>
              {nextLesson && (
                <button onClick={() => setSelectedLesson(nextLesson)} className="btn-secondary text-sm ml-2">
                  Next: {nextLesson.title} →
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
